import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { PlusJakartaSansFontBase64String } from "../../Helpers/PlusJakartaSans-base64String";
import { imgData } from "../../Helpers/smestLogoBase64ImgData";

declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable?: { finalY: number };
    getImageProperties?: (imageData: string | HTMLImageElement) => {
      width: number;
      height: number;
    };
    textWithLink?: (text: string, x: number, y: number, options: any) => any;
    link?: (
      x: number,
      y: number,
      w: number,
      h: number,
      options: { url: string }
    ) => any;
    clip?: () => void;
    discardPath?: () => void;
    saveGraphicsState?: () => void;
    restoreGraphicsState?: () => void;
  }
}

export interface BondInfo {
  isin?: string;
  issuerOfSecurity?: {
    issuerName?: string;
    s3Url?: string;
    issuerType?: { type?: string };
  };
  issuerLogo?: string;
  securityName?: string;
  pricePerBond?: string;
  faceValue?: string;
  taxCategory?: string;
  taxFree?: string | boolean;
  frequencyOfInterest?: string;
  putOptions?: { date?: string; amount?: number | string }[];
  callOptions?: { date?: string }[];
  coupons?: { date?: string; rate?: number | string }[];
  ratingOrganizationMasters?: {
    rating?: { rating?: string; name?: string; type?: string };
    organization?: { name?: string; type?: string };
  }[];
  securityType?: { type?: string };
  securedOrUnsecured?: string;
  guaranteeType?: string;
  couponBasis?: string;
  modeOfIssue?: string;
  exchange?: string;
  nameOfInstrument?: string;
  nriEligibility?: boolean | string;
  ipPayment?: string;
  issueDate?: string;
  nextIPDate?: string;
  recordDate?: number;
  lastIPDate?: string;
  maturities?: { date?: string; amount?: number | string }[];
  activeOn?: string;
  quoteDetails?: {
    sellPrice?: number | string;
    sellYield?: number | string;
  } | null;
}

export interface CalculatedValues {
  price?: number | string;
  yield?: number | string;
  count?: number | string;
  principal?: number | string;
  principle?: number | string;
  accruedInterest?: number | string;
  stampDuty?: number | string;
  totalConsideration?: number | string;
  settlementDate?: string | Date;
}

export interface UserDetails {
  firstName?: string;
  lastName?: string;
  leads?: { leadowner?: string };
  leadName?: string;
}

// ---------- Utilities ----------
export const toINR = (value: any, fraction: number) => {
  const formatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: fraction,
  });
  return formatter.format(value);
};

const INR = (n: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n || 0);

const toNumber = (x?: string | number | null) => {
  if (x == null) return 0;
  if (typeof x === "number") return isFinite(x) ? x : 0;
  const cleaned = x.toString().replace(/[^\d.-]/g, "");
  const n = Number(cleaned);
  return isFinite(n) ? n : 0;
};

const fmt = (x: number | string | undefined | null, digits = 4) => {
  const n = typeof x === "string" ? Number(x) : x ?? 0;
  return isFinite(n as number) ? (n as number).toFixed(digits) : "-";
};

const sortDate = (a: { date?: string }, b: { date?: string }) => {
  const ma = a?.date ? moment(a.date, "DD/MM/YYYY") : null;
  const mb = b?.date ? moment(b.date, "DD/MM/YYYY") : null;
  return (ma?.valueOf() || 0) - (mb?.valueOf() || 0);
};

const faceValueFrom = (bond: BondInfo) => {
  const src = bond.pricePerBond || bond.faceValue || "";
  const first = src.toString().split(" ")[0];
  return toNumber(first);
};

const fetchAsDataURL = async (url?: string): Promise<string | null> => {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: "cors" });
    const blob = await res.blob();
    const reader = new FileReader();
    return await new Promise<string>((resolve) => {
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// Generate QR image as Data URL using 'qrcode'
async function qrDataURL(
  value: string,
  size = 1024,
  margin = 1
): Promise<string | null> {
  try {
    const QR: any = await import("qrcode");
    const dataUrl: string = await QR.toDataURL(value, {
      errorCorrectionLevel: "H",
      margin,
      width: size,
      color: { dark: "#000000", light: "#FFFFFF" },
    });
    return dataUrl;
  } catch (e) {
    console.warn("QR generation failed:", e);
    return null;
  }
}

function slugifyName(name?: string) {
  if (!name) return "";
  return encodeURIComponent(
    name
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/[^A-Za-z0-9\-\.]/g, "")
  );
}

// Draw rounded rect path on a CanvasRenderingContext2D
function canvasRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

// Create a PNG data URL of a rounded gradient button via Canvas (pixel-perfect corners)
function gradientButtonPNG(w: number, h: number, radius = 4): string | null {
  try {
    const scale = 3; // hi-DPI for crispness
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(2, Math.round(w * scale));
    canvas.height = Math.max(2, Math.round(h * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.scale(scale, scale);

    // Clip to rounded rect
    canvasRoundedRect(ctx, 0, 0, w, h, radius);
    ctx.clip();

    // Gradient from left to right with your 3 stops
    const grad = ctx.createLinearGradient(0, 0, w, 0);
    grad.addColorStop(0, "#0052d4");
    grad.addColorStop(0.5, "#4364f7");
    grad.addColorStop(1, "#6fb1fc");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

// Generic rounded card with text
function card(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
  value: string,
  opts: {
    valueEmphasis?: boolean;
    fillColor?: [number, number, number];
    valueColor?: [number, number, number];
    subLabel?: string;
  } = {}
) {
  doc.setDrawColor(225);
  if (opts.fillColor) {
    const [r, g, b] = opts.fillColor;
    doc.setFillColor(r, g, b);
    doc.roundedRect(x, y, w, h, 8, 8, "FD");
  } else {
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, y, w, h, 8, 8, "FD");
  }
  doc.setFontSize(9);
  doc.setTextColor(90);
  doc.setFont(undefined, "bold");
  doc.text(label, x + 12, y + 16, { maxWidth: w - 24 });
  let valueY = y + 32;
  if (opts.subLabel) {
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(120);
    doc.text(opts.subLabel, x + 12, y + 28, { maxWidth: w - 24 });
    valueY = y + 42;
  }
  const vColor =
    opts.valueColor ||
    (opts.valueEmphasis
      ? ([10, 10, 10] as [number, number, number])
      : ([30, 30, 30] as [number, number, number]));
  doc.setFont(undefined, "normal");
  doc.setTextColor(vColor[0], vColor[1], vColor[2]);
  doc.setFontSize(opts.valueEmphasis ? 14 : 11);
  doc.text(value || "-", x + 12, valueY, { maxWidth: w - 24 });
  doc.setTextColor(20);
}

// Draw gradient button by embedding a PNG with perfectly rounded corners
function gradientButton(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  text: string,
  url: string
) {
  const radius = 4;
  const dataUrl = gradientButtonPNG(w, h, radius);
  if (dataUrl) {
    doc.addImage(dataUrl, "PNG", x, y, w, h);
  } else {
    // Fallback to simple colored rounded rect if Canvas not available
    doc.setFillColor(67, 100, 247);
    doc.roundedRect(x, y, w, h, radius, radius, "F");
  }
  // Outline (optional subtle)
  doc.setDrawColor(205);
  doc.roundedRect(x, y, w, h, radius, radius, "S");

  // Button text
  doc.setTextColor(255);
  doc.setFont(undefined, "bold");
  doc.setFontSize(10);
  doc.text(text, x + w / 2, y + h / 2 + 3.2, { align: "center" });

  // Clickable link overlay
  if ((doc as any).link) {
    (doc as any).link(x, y, w, h, { url });
  } else if ((doc as any).textWithLink) {
    (doc as any).textWithLink(text, x + w / 2, y + h / 2 + 3.2, {
      align: "center",
      url,
    });
  }
}

export async function generateBondQuotationFirstPage(
  doc: jsPDF,
  bond: BondInfo,
  calculated: CalculatedValues,
  userDetails: UserDetails
): Promise<jsPDF> {
  const M = 36;
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const GAP = 16;
  const LEFT_W = (W - 2 * M - GAP) * 0.55; // 55%
  const RIGHT_W = (W - 2 * M - GAP) * 0.45; // 45%
  let y = M;

  // Optional font
  if (PlusJakartaSansFontBase64String) {
    doc.addFileToVFS(
      "PlusJakartaSans-VariableFont_wght-normal.ttf",
      PlusJakartaSansFontBase64String
    );
    doc.addFont(
      "PlusJakartaSans-VariableFont_wght-normal.ttf",
      "PlusJakartaSans",
      "normal"
    );
    doc.addFont(
      "PlusJakartaSans-VariableFont_wght-normal.ttf",
      "PlusJakartaSans",
      "bold"
    );
    doc.setFont("PlusJakartaSans", "normal");
  }

  // Company logo (top-right) — preserve aspect ratio
  try {
    if (imgData) {
      const dataUrl = imgData.startsWith("data:")
        ? imgData
        : `data:image/png;base64,${imgData}`;
      const maxW = 140,
        maxH = 48;
      let drawW = 120,
        drawH = 40;
      if (typeof (doc as any).getImageProperties === "function") {
        const p = (doc as any).getImageProperties!(dataUrl);
        const scale = Math.min(maxW / p.width, maxH / p.height);
        drawW = p.width * scale;
        drawH = p.height * scale;
      }
      doc.addImage(dataUrl, "PNG", W - M - drawW, y - 6, drawW, drawH);
    }
  } catch {}

  // Bond/Issuer logo (left of heading)
  const bondLogoUrl = bond.issuerLogo || bond.issuerOfSecurity?.s3Url;
  const bondLogo = await fetchAsDataURL(bondLogoUrl);
  if (bondLogo) {
    doc.addImage(bondLogo, "PNG", M, y, 72, 72);
  }

  // Heading + name + ISIN (no 'ISIN:' label)
  const hx = bondLogo ? M + 84 : M;
  doc.setFontSize(22);
  doc.setFont("PlusJakartaSans", "bold");
  doc.text("Bond Quotation", hx, y + 24);
  doc.setFontSize(12);
  doc.setFont("PlusJakartaSans", "normal");
  const name = bond.securityName || bond.issuerOfSecurity?.issuerName || "";
  if (name) doc.text(name, hx, y + 44, { maxWidth: W - M - hx });
  if (bond.isin) {
    doc.setTextColor(120);
    doc.text(`${bond.isin}`, hx, y + 60);
    doc.setTextColor(20);
  }

  // Extra spacing before meta
  y += bondLogo ? 104 : 84;

  // Prepared For / Date / Prepared By — stacked
  const preparedForName =
    (userDetails as any)?.name?.trim() ||
    [userDetails.firstName, userDetails.lastName].filter(Boolean).join(" ") ||
    "-";
  const preparedFor = `Prepared For: ${preparedForName}`;
  let preparedByName = "";
  try {
    const raw =
      typeof window !== "undefined" ? localStorage.getItem("loginUser") : null;
    if (raw) preparedByName = JSON.parse(raw)?.name || "";
  } catch {}
  if (!preparedByName)
    preparedByName = userDetails.leadName || userDetails.leads?.leadowner || "";
  const preparedBy = preparedByName ? `Prepared By: ${preparedByName}` : "";
  const dateStr = `Date: ${moment().format("MMMM DD, YYYY")}`;

  doc.setFontSize(9);
  doc.setFont("PlusJakartaSans", "normal");
  y += 6;
  doc.text(preparedFor, M, y);
  y += 13;
  doc.text(dateStr, M, y);
  y += 13;
  if (preparedBy) {
    doc.text(preparedBy, M, y);
    y += 15;
  }
  y += 8;

  // Numbers & computed values
  const fv = faceValueFrom(bond);
  const sellPrice = toNumber(calculated.price ?? bond.quoteDetails?.sellPrice);
  const sellYield = toNumber(calculated.yield ?? bond.quoteDetails?.sellYield);
  const units = toNumber(calculated.count);
  const principal = toNumber(calculated.principal ?? calculated.principle);
  const accrued = toNumber(calculated.accruedInterest);
  const stamp = toNumber(calculated.stampDuty);
  const totalConsideration = toNumber(calculated.totalConsideration);
  const totalInvestment = totalConsideration + stamp;
  const lastIP = bond.lastIPDate
    ? moment(bond.lastIPDate).format("DD MMM, YYYY")
    : "-";
  const settlement = calculated.settlementDate
    ? moment(calculated.settlementDate).format("DD MMM, YYYY")
    : "-";

  // ---------------- LEFT COLUMN: Key Bond Information ----------------
  const leftStartY = y;

  const couponStr =
    bond.coupons?.[0]?.rate != null && bond.coupons?.[0]?.rate !== ""
      ? `${Number(bond.coupons[0].rate)}%`
      : "-";
  const rat0 = bond.ratingOrganizationMasters?.[0];
  const ratingName =
    rat0?.rating?.rating || rat0?.rating?.name || rat0?.rating?.type || "-";
  const ratingOrg = rat0?.organization?.name || rat0?.organization?.type || "";
  const ratingStr =
    ratingName !== "-"
      ? `${ratingName}${ratingOrg ? ` by ${ratingOrg}` : ""}`
      : "-";

  const dateToMatureOrCall = bond.callOptions?.at(-1)?.date
    ? {
        date: moment(
          bond.callOptions?.sort((a, b) => sortDate(a, b)).at(-1)?.date,
          "DD/MM/YYYY"
        ),
        type: "Call Date",
        to: "call" as const,
      }
    : {
        date: moment(
          bond.maturities?.sort((a, b) => sortDate(a, b)).at(-1)?.date,
          "DD/MM/YYYY"
        ),
        type: "Maturity Date",
        to: "mature" as const,
      };
  const now = moment();
  const diff = moment.duration(dateToMatureOrCall?.date?.diff(now));
  const years = diff ? diff.years() : 0;
  const months = diff ? diff.months() : 0;
  const days = diff ? diff.days() : 0;
  const formattedDiff =
    years && months && days
      ? `${years} Yr ${months} Mo ${days} D`
      : !years && months && days
      ? `${months} Mo ${days} D`
      : !years && !months && days
      ? `${days} D`
      : years && !months && !days
      ? `${years} Yr`
      : years && months && !days
      ? `${years} Yr ${months} Mo`
      : years && !months && days
      ? `${years} Yr ${days} D`
      : !years && months && !days
      ? `${months} Mo`
      : `${years} Yr ${months} Mo ${days} D`;
  const residualLine = dateToMatureOrCall?.date?.isValid()
    ? `${formattedDiff} to ${dateToMatureOrCall.to}`
    : "-";

  const allotmentFmt = bond.issueDate
    ? moment(bond.issueDate, "YYYY-MM-DD").format("DD MMM, YYYY")
    : "-";
  const maturityLast = bond.maturities?.length
    ? moment(
        bond.maturities.sort((a, b) => sortDate(a, b)).at(-1)?.date,
        "DD/MM/YYYY"
      ).format("DD MMM, YYYY")
    : "-";
  const callLast = bond.callOptions?.length
    ? moment(
        bond.callOptions.sort((a, b) => sortDate(a, b)).at(-1)?.date,
        "DD/MM/YYYY"
      ).format("DD MMM, YYYY")
    : "-";
  const putDate = bond.putOptions?.length
    ? moment(
        bond.putOptions.sort((a, b) => sortDate(a, b)).at(0)?.date,
        "DD/MM/YYYY"
      ).format("DD MMM, YYYY")
    : "-";
  const nextIpFmt = bond.nextIPDate
    ? moment(bond.nextIPDate, "YYYY-MM-DD").format("DD MMM, YYYY")
    : "-";
  const recordDateFmt =
    bond.recordDate != null && bond.nextIPDate
      ? moment(bond.nextIPDate, "YYYY-MM-DD")
          .subtract(bond.recordDate, "days")
          .format("DD MMM, YYYY")
      : "-";
  const faceValStr = bond.faceValue
    ? toINR(bond.faceValue.split(" ")[0], 4)
    : fv
    ? toINR(fv, 4)
    : "-";

  const keyRows: [string, string][] = [
    ["Coupon", couponStr],
    ["Rating", ratingStr],
    ["Bond Type", bond.securedOrUnsecured || "-"],
    ["Tax Category", bond.taxCategory || "-"],
    ["Residual Maturity", residualLine],
    ["Interest Frequency", bond.frequencyOfInterest || "-"],
    ["Allotment Date", allotmentFmt],
    ["Maturity Date", maturityLast],
    ["Call Date", callLast],
    ["Put Date", putDate],
    ["Next Interest Payment Date", nextIpFmt],
    ["Shut Period", bond.recordDate != null ? `${bond.recordDate} days` : "-"],
    ["Active On CDSL or NSDL", bond.activeOn || "-"],
    ["Type of Guarantee", bond.guaranteeType || "-"],
    ["Perpetual?", bond.nameOfInstrument === "Non Perpetual" ? "No" : "Yes"],
    ["Mode of Issuance", bond.modeOfIssue || "-"],
    ["Listing Details", bond.exchange === "Unlisted" ? "Unlisted" : "Listed"],
    ["Record Date", recordDateFmt],
    ["NRI Eligibility", bond.nriEligibility === true ? "Yes" : "No"],
    ["Face Value", faceValStr],
  ];

  (doc as any).autoTable({
    startY: leftStartY,
    head: [["Key Bond Information", ""]],
    body: keyRows,
    theme: "grid",
    styles: {
      font: PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
      fontSize: 10,
      cellPadding: 6,
      lineWidth: 0.1,
      lineColor: 210,
      textColor: 30,
    },
    headStyles: {
      fillColor: [248, 248, 248],
      textColor: 20,
      fontStyle: "bold",
      halign: "left",
    },
    columnStyles: {
      0: { cellWidth: LEFT_W * 0.45, fontStyle: "bold" },
      1: { cellWidth: LEFT_W * 0.55 },
    },
    margin: { left: M, right: W - (M + LEFT_W) },
    tableWidth: LEFT_W,
  });

  const leftEndY = (doc as any).lastAutoTable.finalY;

  // ---------------- RIGHT COLUMN ----------------
  const rx = M + LEFT_W + GAP;
  let ry = leftStartY;

  // Row 1: Price & Yield (two halves)
  const kGap = 10;
  const halfW = (RIGHT_W - kGap) / 2;
  const kH = 60;
  card(doc, rx, ry, halfW, kH, "Clean Price", `₹${fmt(sellPrice, 4)}`);

  // Dynamic yield tag: YTC if any callOptions; else YTP if first put==first maturity (amount+date); else YTM
  const hasCall = !!bond?.callOptions?.length;
  const hasPutMatMatch = !!(
    bond?.putOptions?.length &&
    bond?.maturities?.length &&
    (bond.putOptions?.[0]?.amount?.toString() ?? "") ===
      (bond.maturities?.[0]?.amount?.toString() ?? "") &&
    (bond.putOptions?.[0]?.date ?? "") === (bond.maturities?.[0]?.date ?? "")
  );
  const yieldTag = hasCall ? "YTC" : hasPutMatMatch ? "YTP" : "YTM";
  card(
    doc,
    rx + halfW + kGap,
    ry,
    halfW,
    kH,
    `Yield (${yieldTag})`,
    `${fmt(sellYield, 4)}%`
  );
  ry += kH + 12;

  // Row 2A: Number of units & Settlement Date
  const tileH = 56;
  card(doc, rx, ry, halfW, tileH, "Number of units", String(units || 0));
  card(doc, rx + halfW + kGap, ry, halfW, tileH, "Settlement Date", settlement);
  ry += tileH + 10;

  // Row 2B: Principal Amount (full width)
  const fullW = RIGHT_W;
  const fullH = 56;
  card(
    doc,
    rx,
    ry,
    fullW,
    fullH,
    "Principal Amount",
    INR(principal || units * fv),
    { valueEmphasis: true }
  );
  ry += fullH + 10;

  // Row 2C: Accrued Interest (half, with sublabel) + Stamp Duty (half)
  const accruedH = 68;
  card(doc, rx, ry, halfW, accruedH, "Accrued Interest", INR(accrued), {
    subLabel: lastIP !== "-" ? `from ${lastIP}` : undefined,
  });
  card(doc, rx + halfW + kGap, ry, halfW, accruedH, "Stamp Duty", INR(stamp));
  ry += accruedH + 10;

  // Row 2D: Total Investment (full width, GREEN emphasis)
  card(doc, rx, ry, fullW, 56, "Total Investment", INR(totalInvestment), {
    fillColor: [235, 252, 240],
    valueColor: [34, 120, 66],
    valueEmphasis: true,
  });
  ry += 56 + 10;

  // Row 2E: QR Card (white bg, rounded, full-width QR + 'or' + gradient button)
  const nameSlug = slugifyName(bond.securityName);
  const bondUrl =
    bond.isin && nameSlug
      ? `https://smest.in/bond-details/${nameSlug}/${bond.isin}`
      : "https://smest.in/bond-details";

  // Compute desired height to let QR take full row width
  const labelH = 18,
    topAfterLabel = 10,
    betweenQRAndOr = 8,
    orH = 9,
    betweenOrAndBtn = 8,
    btnH = 22,
    bottomPad = 12;
  const maxQr = fullW - 24; // w - 2*padX
  const desiredH =
    labelH +
    topAfterLabel +
    maxQr +
    betweenQRAndOr +
    orH +
    betweenOrAndBtn +
    btnH +
    bottomPad;
  const available = H - M - ry - 6;
  const drawH = Math.max(80, Math.min(desiredH, available));

  // QR card drawing inline to keep gradient button helper
  // White background, rounded border
  doc.setDrawColor(225);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(rx, ry, fullW, drawH, 8, 8, "FD");

  // Label
  doc.setFontSize(9);
  doc.setTextColor(60);
  doc.setFont(undefined, "bold");
  doc.text("Scan to Invest", rx + fullW / 2, ry + 16, { align: "center" });

  // QR
  const padX = 12;
  const qrSize = Math.max(
    40,
    Math.min(
      fullW - 2 * padX,
      drawH -
        (labelH +
          topAfterLabel +
          betweenQRAndOr +
          orH +
          betweenOrAndBtn +
          btnH +
          bottomPad)
    )
  );
  const qx = rx + (fullW - qrSize) / 2;
  const qy = ry + labelH + topAfterLabel;
  const qrUrl = await qrDataURL(bondUrl, 1024, 1);
  if (qrUrl)
    doc.addImage(qrUrl, "PNG", qx, qy, qrSize, qrSize, undefined, "FAST");

  // 'or'
  const orY = qy + qrSize + betweenQRAndOr + 6;
  doc.setFontSize(8);
  doc.setTextColor(130);
  doc.setFont(undefined, "normal");
  doc.text("or", rx + fullW / 2, orY, { align: "center" });

  // Button
  const bx = rx + padX;
  const by = orY + betweenOrAndBtn;
  const bw = fullW - 2 * padX;
  const bh = btnH;
  gradientButton(doc, bx, by, bw, bh, "Click to Invest", bondUrl);

  ry += drawH;

  const bottom = Math.max(leftEndY, ry) + 10;
  doc.setDrawColor(235);
  doc.line(M, bottom, W - M, bottom);

  return doc;
}
